/**
 * KAN-759: общая рамка модалок заказа (просмотр и формирование в CRM):
 * загрузка заказа по id, номер, статус, даты, ссылка на страницу заказа.
 * Содержимое (позиции, кнопки) отдаёт children по загруженному заказу.
 * KAN-903: footer - кнопки рамки, видны и до загрузки заказа (и при ошибке).
 * Ссылка на страницу заказа только тем, кому открыт раздел /orders (менеджеру
 * корзина клиента доступна, раздел заказов нет: useCanOpenPath).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCanOpenPath } from '@/hooks/useApiAccess';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useOrder } from '@/hooks/useOrders';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { OrderDetail } from '@/types/order';
import { formatOrderDate, orderStatusView, orderTitle } from './orderFormat';

interface OrderDialogShellProps {
  /** null: модалка закрыта. */
  orderId: string | null;
  onClose: () => void;
  testId: string;
  children: (order: OrderDetail) => ReactNode;
  footer?: ReactNode;
}

function OrderDialogBody({ orderId, children }: { orderId: string; children: OrderDialogShellProps['children'] }) {
  const query = useOrder(orderId);
  const order = query.data;
  const orderPath = `/orders/${encodeURIComponent(orderId)}`;
  const canOpenOrder = useCanOpenPath(orderPath);

  if (!order) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Заказ</DialogTitle>
        </DialogHeader>
        {query.isLoading ? (
          <div className="flex justify-center py-8" data-testid="order-dialog-loading">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <p className="text-sm text-gray-600" data-testid="order-dialog-error">
            {isNotFoundError(query.error)
              ? 'Заказ не найден или недоступен.'
              : extractErrorMessage(query.error, 'Не удалось загрузить заказ')}
          </p>
        )}
      </>
    );
  }

  const status = orderStatusView(order.status);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          {orderTitle(order.short_id)}
          <Badge variant={status.variant} data-testid="order-status">
            {status.label}
          </Badge>
        </DialogTitle>
        <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1">
          <span>Создан: {formatOrderDate(order.created_at)}</span>
          {order.submitted_at && <span>Оформлен: {formatOrderDate(order.submitted_at)}</span>}
        </DialogDescription>
      </DialogHeader>
      <div className="-mx-6 min-w-0">{children(order)}</div>
      {canOpenOrder && (
        <div className="text-sm">
          <Link to={orderPath} className="text-blue-600 hover:underline">
            Открыть страницу заказа
          </Link>
        </div>
      )}
    </>
  );
}

export function OrderDialogShell({ orderId, onClose, testId, children, footer }: OrderDialogShellProps) {
  return (
    <Dialog open={orderId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid={testId}>
        {orderId !== null && <OrderDialogBody orderId={orderId}>{children}</OrderDialogBody>}
        {orderId !== null && footer}
      </DialogContent>
    </Dialog>
  );
}
